// Shared "use precomputed cache" picker for the Kronos gate. Lists every
// precomputed cache for a market (one per signature × decision_time) and,
// on select, hands the chosen signature back so the caller can write the six
// inference params (+ decision time) into its config — guaranteeing the gate
// hits the cache instead of silently cold-starting to 0 trades.
//
// It also surfaces a warning when the caller's CURRENT signature/decision
// time has no matching cache, which is the exact footgun that produces
// "Kronos on -> 0/0" results.
import { useQuery } from "@tanstack/react-query";
import { Alert, Loader, Select, Stack, Text } from "@mantine/core";

import { api } from "../api/client";
import type { KronosSignatureInfo } from "../api/kronosSchemas";

function fmtDate(iso: string | null): string {
  if (!iso) return "?";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "?";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function optionValue(info: { signature: string; decision_time_seconds: number }): string {
  return `${info.signature}@@${info.decision_time_seconds}`;
}

export function KronosCachePicker({
  marketId,
  currentSignature,
  currentDecisionTime,
  filterByDecisionTime = false,
  onApply,
}: {
  marketId: string | null | undefined;
  /** The caller's current cache signature, to detect a mismatch. */
  currentSignature?: string | null;
  /** The caller's current decision_time_seconds, to detect a mismatch. */
  currentDecisionTime?: number | null;
  /**
   * When true, the dropdown ONLY lists caches whose decision_time_seconds
   * matches ``currentDecisionTime``. If no decision time is entered, or none
   * of the market's caches match it, the picker is disabled and shows a
   * warning telling the operator to change the value or precompute a cache.
   * Used by the Manual Simulator, which runs a single fixed decision time.
   */
  filterByDecisionTime?: boolean;
  /** Called with the selected cache so the caller can apply its params. */
  onApply: (info: KronosSignatureInfo) => void;
}) {
  const q = useQuery({
    queryKey: ["kronos-signatures", marketId],
    queryFn: () => api.kronos.signatures(marketId as string),
    enabled: Boolean(marketId),
    staleTime: 15_000,
  });

  const allItems = q.data?.items ?? [];
  const dt = currentDecisionTime != null ? Math.trunc(currentDecisionTime) : null;
  // In filter mode, only surface caches for the entered decision time so the
  // operator can't pick one that would cold-start the gate at their dt.
  const items =
    filterByDecisionTime && dt != null
      ? allItems.filter((it) => Math.trunc(it.decision_time_seconds) === dt)
      : filterByDecisionTime
        ? [] // decision time not entered yet -> nothing to choose
        : allItems;

  const options = items.map((it) => ({
    value: optionValue(it),
    // Prefix the forecast MODE so two caches with identical six params but
    // different modes (settled vs decide-late) are distinguishable — they are
    // separate cache signatures (|md=settled) and must be picked deliberately.
    label:
      `${it.settled ? "⚙ settled" : "decide-late"} · ${it.model} · ` +
      `lb=${it.lookback} tf=${it.timeframe} sc=${it.sample_count} ` +
      `T=${it.temperature} tp=${it.top_p} · dt=${it.decision_time_seconds} · ` +
      `${it.count} win · ${fmtDate(it.window_start_min)}–${fmtDate(it.window_start_max)}`,
  }));

  const currentKey =
    currentSignature && currentDecisionTime != null
      ? `${currentSignature}@@${Math.trunc(currentDecisionTime)}`
      : null;
  const matched = currentKey != null && items.some((it) => optionValue(it) === currentKey);
  const hasItems = items.length > 0;
  // Filter-mode diagnostics.
  const needsDecisionTime = filterByDecisionTime && dt == null;
  const marketHasAnyCache = allItems.length > 0;
  const noCacheForDt = filterByDecisionTime && dt != null && !hasItems;

  const description = filterByDecisionTime
    ? "Pick a Kronos cache precomputed for the decision time entered above. Selecting one fills in the six signature params so the gate reads real forecasts instead of cold-starting."
    : "Pick an existing Kronos cache for this market. Selecting one fills in the six signature params and decision time so the gate reads real forecasts instead of cold-starting.";

  return (
    <Stack gap={6}>
      <Select
        label="Use precomputed cache"
        description={description}
        placeholder={
          !marketId
            ? "Select a market first"
            : q.isLoading
              ? "Loading caches…"
              : needsDecisionTime
                ? "Enter a decision time above first"
                : hasItems
                  ? "Select a cache"
                  : filterByDecisionTime
                    ? `No cache for decision time ${dt}s`
                    : "No caches precomputed for this market"
        }
        data={options}
        value={matched ? currentKey : null}
        disabled={!marketId || q.isLoading || !hasItems}
        searchable
        nothingFoundMessage="No matching cache"
        rightSection={q.isLoading ? <Loader size="xs" /> : undefined}
        onChange={(v) => {
          if (!v) return;
          const found = items.find((it) => optionValue(it) === v);
          if (found) onApply(found);
        }}
      />

      {marketId && !q.isLoading && needsDecisionTime ? (
        <Alert color="yellow" variant="light" p="xs">
          <Text size="sm">
            Enter a <b>decision time</b> above to see the Kronos caches available for it.
          </Text>
        </Alert>
      ) : null}

      {marketId && !q.isLoading && noCacheForDt ? (
        <Alert color="red" variant="light" p="xs">
          <Text size="sm">
            {marketHasAnyCache ? (
              <>
                No Kronos cache exists for <b>{marketId}</b> at decision time <b>{dt}s</b>. Change
                the decision time to one that is cached, or precompute this decision time in{" "}
                <b>Settings → Kronos</b> — otherwise the gate cold-starts and skips every window.
              </>
            ) : (
              <>
                No precomputed Kronos caches exist for <b>{marketId}</b> at all. Run one in{" "}
                <b>Settings → Kronos</b> before enabling the gate, or it will skip every window.
              </>
            )}
          </Text>
        </Alert>
      ) : null}

      {marketId && !q.isLoading && !filterByDecisionTime && !hasItems ? (
        <Alert color="yellow" variant="light" p="xs">
          <Text size="sm">
            No precomputed Kronos caches exist for <b>{marketId}</b>. Run one in{" "}
            <b>Settings → Kronos</b> before enabling the gate, or it will skip every window.
          </Text>
        </Alert>
      ) : null}

      {hasItems && currentSignature && !matched ? (
        <Alert color="red" variant="light" p="xs">
          <Text size="sm">
            The current config signature <b>{currentSignature}</b>
            {currentDecisionTime != null ? ` @ dt=${Math.trunc(currentDecisionTime)}` : ""} has{" "}
            <b>no matching cache</b> — the gate will cold-start and skip every window (0 trades).
            Pick a cache above to fix it.
          </Text>
        </Alert>
      ) : null}
    </Stack>
  );
}
